// LibrarySearch selectors

import { createSelector } from 'reselect';
import { selectSortedSubjects } from '../../routes/Library/selectors';

export const selectMathSuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'math'),
);

export const selectGermanSuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'german'),
);

export const selectEnglishSuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'english'),
);

export const selectLatinSuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'latin'),
);

export const selectFrenchSuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'french'),
);

export const selectBiologySuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'biology'),
);

export const selectChemistrySuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'chemistry'),
);

export const selectPhysicsSuggestions = createSelector(
    (state) => state.librarySearch.suggestions.hits,
    (suggestions) => suggestions.filter((suggestion) => suggestion.fields.subjectIdentifier === 'physics'),
);

export const selectChosenSuggestionSubject = createSelector(
    (state) => state.librarySearch.chosenSuggestionSubjectId,
    selectSortedSubjects,
    (chosenSuggestionSubjectId, subjects) => subjects.find((subject) => subject.id === chosenSuggestionSubjectId),
);

export const selectSubjectSuggestionLengthObj = createSelector(
    selectMathSuggestions,
    selectGermanSuggestions,
    selectEnglishSuggestions,
    selectLatinSuggestions,
    selectFrenchSuggestions,
    selectBiologySuggestions,
    selectChemistrySuggestions,
    selectPhysicsSuggestions,
    // eslint-disable-next-line max-len
    (mathSuggestions, germanSuggestions, englishSuggestions, latinSuggestions, frenchSuggestions, biologySuggestions, chemistrySuggestions, physicsSuggestions) => ({
        math: mathSuggestions.length,
        german: germanSuggestions.length,
        english: englishSuggestions.length,
        latin: latinSuggestions.length,
        french: frenchSuggestions.length,
        biology: biologySuggestions.length,
        chemistry: chemistrySuggestions.length,
        physics: physicsSuggestions.length,
    }),
);

export const selectSubjectsWithSuggestions = createSelector(
    selectSortedSubjects,
    selectMathSuggestions,
    selectGermanSuggestions,
    selectEnglishSuggestions,
    selectLatinSuggestions,
    selectFrenchSuggestions,
    selectBiologySuggestions,
    selectChemistrySuggestions,
    selectPhysicsSuggestions,
    (subjects, mathSuggestions, germanSuggestions, englishSuggestions, latinSuggestions,
        frenchSuggestions, biologySuggestions, chemistrySuggestions, physicsSuggestions) => subjects
        .filter((subject) => (mathSuggestions.length > 0 && subject.identifier === 'math')
            || (germanSuggestions.length > 0 && subject.identifier === 'german')
            || (englishSuggestions.length > 0 && subject.identifier === 'english')
            || (latinSuggestions.length > 0 && subject.identifier === 'latin')
            || (frenchSuggestions.length > 0 && subject.identifier === 'french')
            || (biologySuggestions.length > 0 && subject.identifier === 'biology')
            || (chemistrySuggestions.length > 0 && subject.identifier === 'chemistry')
            || (physicsSuggestions.length > 0 && subject.identifier === 'physics')),
);

// selectProps...
export const selectLibrarySearchProps = createSelector(
    (state) => state.librarySearch,
    (state) => state.librarySearch.activeSuggestionNavigation,
    (form, activeSuggestionNavigation) => ({
        form,
        activeSuggestionNavigation,
    }),
);

export const selectLibrarySuggestionsProps = createSelector(
    (state) => state.librarySearch.suggestions.totalHitsCount,
    selectChosenSuggestionSubject,
    selectMathSuggestions,
    selectGermanSuggestions,
    selectEnglishSuggestions,
    selectLatinSuggestions,
    selectFrenchSuggestions,
    selectBiologySuggestions,
    selectChemistrySuggestions,
    selectPhysicsSuggestions,
    selectSubjectsWithSuggestions,
    (suggestionsHits, chosenSuggestionSubject, mathSuggestions, germanSuggestions, englishSuggestions, latinSuggestions,
        frenchSuggestions, biologySuggestions, chemistrySuggestions, physicsSuggestions, subjectsWithSuggestions) => ({
        suggestionsHits,
        chosenSuggestionSubject,
        mathSuggestions,
        germanSuggestions,
        englishSuggestions,
        latinSuggestions,
        frenchSuggestions,
        biologySuggestions,
        chemistrySuggestions,
        physicsSuggestions,
        subjectsWithSuggestions,
    }),
);

export const selectSuggestionsProps = createSelector(
    (state) => state.librarySearch.suggestions.totalHitsCount,
    selectMathSuggestions,
    selectGermanSuggestions,
    selectEnglishSuggestions,
    selectLatinSuggestions,
    selectFrenchSuggestions,
    selectBiologySuggestions,
    selectChemistrySuggestions,
    selectPhysicsSuggestions,
    (suggestionsHitsCount, mathSuggestions, germanSuggestions, englishSuggestions, latinSuggestions,
        frenchSuggestions, biologySuggestions, chemistrySuggestions, physicsSuggestions) => ({
        suggestionsHitsCount,
        mathSuggestions,
        germanSuggestions,
        englishSuggestions,
        latinSuggestions,
        frenchSuggestions,
        biologySuggestions,
        chemistrySuggestions,
        physicsSuggestions,
    }),
);
